import { CreditCard } from 'lucide-react';

// Operator primitive — Plus payment-plan indicator.
// Renders only when a payment plan exists for the Builder. The 2-pay/3-pay launch CTA is
// feature-flagged off; this component MUST NOT advertise plan availability when no plan
// row exists for this Builder.

export interface PlusPaymentPlanIndicatorPlan {
  planId: string;
  status: string;
  installmentsTotal: number;
  installmentsPaid: number;
  amountTotal: number;
  amountPaid?: number | null;
  currency: string;
  featureFlagEnabled: boolean;
}

export interface PlusPaymentPlanIndicatorProps {
  plans: PlusPaymentPlanIndicatorPlan[];
  className?: string;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 0,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(0)}`;
  }
}

function describePlan(planId: string): string {
  if (planId.includes('2pay')) return '2-pay split';
  if (planId.includes('3pay')) return '3-pay split';
  return 'Payment plan';
}

export function PlusPaymentPlanIndicator({ plans, className }: PlusPaymentPlanIndicatorProps) {
  // No plans = no UI. Per spec: do not advertise plan availability when none exists.
  if (!plans || plans.length === 0) return null;

  return (
    <div
      className={[
        'rounded-[10px] border border-fd-border bg-fd-paper p-4',
        className ?? '',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] border border-fd-border bg-fd-bg text-fd-text-muted">
          <CreditCard className="h-3.5 w-3.5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-geist-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-fd-text-subtle">
            Plus payment plan
          </p>
          <ul className="mt-1.5 grid gap-2">
            {plans.map((plan) => {
              const remaining = Math.max(
                0,
                plan.installmentsTotal - plan.installmentsPaid,
              );
              return (
                <li key={plan.planId} className="grid gap-0.5">
                  <p className="font-geist text-[13.5px] font-medium leading-[1.4] text-fd-text">
                    {describePlan(plan.planId)} · {plan.installmentsPaid}/
                    {plan.installmentsTotal} installments paid
                  </p>
                  <p className="text-[12.5px] leading-[1.45] text-fd-text-muted">
                    {plan.featureFlagEnabled
                      ? `${remaining === 0 ? 'Complete' : `${remaining} installment${remaining === 1 ? '' : 's'} remaining`} · status: ${plan.status}`
                      : 'Status: feature-flag-off · this plan was created before split-pay was generally available, and remains supported on your account.'}
                  </p>
                  <p className="font-geist-mono text-[11px] text-fd-text-subtle">
                    {formatCurrency(plan.amountPaid ?? 0, plan.currency)} of{' '}
                    {formatCurrency(plan.amountTotal, plan.currency)}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
